import { makeAutoObservable, runInAction } from 'mobx';
import { ErrorStatusEnum } from '@app/common';
import type { RootStore } from './root-store';
import i18n from '../../translation/i18n';
import { pollCheckoutStatus, waitForCheckoutStatusRetry } from '../../utils/poll-checkout-status';

const PROFILE_REFRESH_BACKOFF_MS = [0, 500, 1000, 2000, 4000];

export type PostCheckoutFlowPhase = 'idle' | 'waitingForTerms' | 'thanks' | 'survey' | 'profile' | 'complete';

export const getUrlWithoutCheckoutParams = (href: string, pathname?: string): string => {
    const url = new URL(href);
    ['success', 'billing_period', 'session_id'].forEach((param) => url.searchParams.delete(param));
    if (pathname) {
        url.pathname = pathname;
    }
    return `${url.pathname}${url.search}${url.hash}`;
};

type CheckoutDetails = {
    identity: string;
    sessionId: string | null;
};

export class PostCheckoutFlowStore {
    currentUserId: string | null = null;

    checkout: CheckoutDetails | null = null;

    phase: PostCheckoutFlowPhase = 'idle';

    showSurvey = true;

    sourceSaved = false;

    fullName = '';

    nickname = '';

    loading = false;

    error: string | null = null;

    private operation = 0;

    private checkoutPaymentConfirmed = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    get isProfileStep(): boolean {
        return this.phase === 'profile';
    }

    setFullName(value: string): void {
        this.fullName = value;
        this.error = null;
    }

    setNickname(value: string): void {
        this.nickname = value;
        this.error = null;
    }

    clearError(): void {
        this.error = null;
    }

    get isActive(): boolean {
        return this.checkout !== null && this.phase !== 'idle' && this.phase !== 'complete';
    }

    get isThanksOpened(): boolean {
        return this.phase === 'thanks';
    }

    get isSurveyStep(): boolean {
        return this.phase === 'survey';
    }

    synchronizeUser(userId: string): void {
        if (this.currentUserId === userId) {
            return;
        }

        this.reset();
        this.currentUserId = userId;
    }

    initializeFromSearch(search: string, userHasPaidTariff: boolean, agreedTerms: boolean): void {
        const params = new URLSearchParams(search);
        const success = params.get('success');

        if (success !== 'true' && success !== '1') {
            return;
        }

        const sessionId = params.get('session_id')?.trim();
        // Other actions (for example a plan upgrade) also use a generic success URL.
        if (!sessionId) return;
        const identity = sessionId;
        const checkout = {
            identity,
            sessionId,
        };

        if (this.checkout?.identity === identity) {
            if (this.checkoutPaymentConfirmed) {
                this.resumeAfterTerms(agreedTerms);
            }
            return;
        }

        this.waitForVerifiedCheckout(checkout, userHasPaidTariff, agreedTerms).catch(() => undefined);
    }

    private activateCheckout(checkout: CheckoutDetails, agreedTerms: boolean): void {
        if (this.checkout?.identity === checkout.identity && this.checkoutPaymentConfirmed) {
            this.resumeAfterTerms(agreedTerms);
            return;
        }

        this.checkout = checkout;
        this.checkoutPaymentConfirmed = true;
        const profile = this.rootStore.authStore.userProfile;
        this.showSurvey = !profile?.sourceSurvey;
        this.sourceSaved = Boolean(profile?.sourceSurvey);
        this.fullName = profile?.fullName ?? '';
        this.nickname = profile?.nickname ?? '';
        this.error = null;
        this.operation += 1;
        this.loading = false;
        this.phase = agreedTerms ? 'thanks' : 'waitingForTerms';
    }

    private async waitForVerifiedCheckout(
        checkout: CheckoutDetails,
        userHasPaidTariff: boolean,
        agreedTerms: boolean,
    ): Promise<void> {
        this.checkout = checkout;
        this.checkoutPaymentConfirmed = false;
        this.operation += 1;
        const { operation } = this;
        const userId = this.currentUserId;
        this.phase = 'waitingForTerms';
        const isCurrent = (): boolean => this.isCurrentCheckoutRefresh(checkout.identity, operation, userId);
        const completed = await pollCheckoutStatus(
            checkout.sessionId,
            (sessionId) => this.rootStore.apiClient.stripeController.stripeControllerGetCheckoutStatus({ sessionId }),
            waitForCheckoutStatusRetry,
            isCurrent,
        );
        if (!isCurrent()) return;
        if (!completed) {
            runInAction(() => {
                this.checkout = null;
                this.phase = 'idle';
            });
            return;
        }
        if (userHasPaidTariff) {
            runInAction(() => this.activateCheckout(checkout, agreedTerms));
            return;
        }
        await this.refreshProfileUntilPaid(checkout, operation, userId);
    }

    private async refreshProfileUntilPaid(
        checkout: CheckoutDetails,
        operation: number,
        userId: string | null,
        attempt = 0,
    ): Promise<void> {
        const delayMs = PROFILE_REFRESH_BACKOFF_MS[attempt];
        if (delayMs > 0) {
            await new Promise<void>((resolve) => {
                setTimeout(resolve, delayMs);
            });
        }
        if (!this.isCurrentCheckoutRefresh(checkout.identity, operation, userId)) return;

        await this.rootStore.authStore.fetchUserProfile();
        if (!this.isCurrentCheckoutRefresh(checkout.identity, operation, userId)) return;
        if (this.rootStore.authStore.userHasPaidTariff) {
            const agreedTerms = this.rootStore.authStore.userProfile?.agreedTerms ?? false;
            runInAction(() => this.activateCheckout(checkout, agreedTerms));
            return;
        }
        if (attempt + 1 < PROFILE_REFRESH_BACKOFF_MS.length) {
            await this.refreshProfileUntilPaid(checkout, operation, userId, attempt + 1);
            return;
        }

        if (this.isCurrentCheckoutRefresh(checkout.identity, operation, userId)) {
            runInAction(() => {
                this.checkout = null;
                this.phase = 'idle';
            });
        }
    }

    private isCurrentCheckoutRefresh(identity: string, operation: number, userId: string | null): boolean {
        return (
            !this.checkoutPaymentConfirmed &&
            this.operation === operation &&
            this.currentUserId === userId &&
            this.rootStore.authStore.userProfile?.id === userId &&
            this.checkout?.identity === identity
        );
    }

    resumeAfterTerms(agreedTerms: boolean): void {
        if (agreedTerms && this.checkoutPaymentConfirmed && this.phase === 'waitingForTerms') {
            this.phase = 'thanks';
        }
    }

    advanceFromThanks(sourceSurveyAnswered: boolean): boolean {
        if (this.phase !== 'thanks') {
            return false;
        }

        this.phase = sourceSurveyAnswered ? 'profile' : 'survey';
        return sourceSurveyAnswered;
    }

    completeSurvey(): boolean {
        if (this.phase !== 'survey') {
            return false;
        }

        this.sourceSaved = true;
        this.phase = 'profile';
        return true;
    }

    back(): void {
        if (this.loading) return;
        this.error = null;
        if (this.phase === 'profile') {
            this.phase = this.showSurvey ? 'survey' : 'thanks';
        } else if (this.phase === 'survey') {
            this.phase = 'thanks';
        }
    }

    complete(): void {
        this.operation += 1;
        this.phase = 'complete';
        this.loading = false;
        this.error = null;
    }

    async submitSource(): Promise<boolean> {
        if (this.phase !== 'survey' || this.loading) return false;
        if (this.sourceSaved) return this.completeSurvey();
        const { source } = this.rootStore.sourceSurveyStore;
        if (!source) return false;
        this.operation += 1;
        const { operation } = this;
        const userId = this.currentUserId;
        this.loading = true;
        this.error = null;
        try {
            await this.rootStore.apiClient.userController.userControllerUpdateSourceSurvey({
                sourceSurveyDto: { sourceSurvey: source },
            });
            if (!this.isCurrentOperation(operation, userId)) return false;
            runInAction(() => {
                const profile = this.rootStore.authStore.userProfile;
                if (profile) profile.sourceSurvey = source;
                this.completeSurvey();
            });
            return true;
        } catch (error) {
            if (this.isCurrentOperation(operation, userId)) {
                runInAction(() => this.setRequestError(error));
            }
            return false;
        } finally {
            if (this.isCurrentOperation(operation, userId)) {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    }

    async saveProfile(): Promise<boolean> {
        const profile = this.rootStore.authStore.userProfile;
        if (this.phase !== 'profile' || this.loading || !profile) return false;
        const nickname = this.nickname.trim() || profile.nickname;
        if (!nickname) {
            this.error = i18n.t(`errors.${ErrorStatusEnum.NicknameIsEmptyError}`);
            return false;
        }
        if (nickname !== profile.nickname && nickname.split(/\s+/).length > 2) {
            this.error = i18n.t(`errors.${ErrorStatusEnum.NicknameWordsQuantityError}`);
            return false;
        }
        const values = { fullName: this.fullName.trim(), nickname, email: profile.email };
        if (values.fullName.length > 255 || nickname.length > 255) {
            this.error = i18n.t('dialogs.onboarding.profileTooLong');
            return false;
        }
        this.operation += 1;
        const { operation } = this;
        const userId = this.currentUserId;
        this.loading = true;
        this.error = null;
        try {
            const { nicknameAlreadyTaken } =
                await this.rootStore.apiClient.userController.userControllerIsNicknameAlreadyTaken({ nickname });
            if (!this.isCurrentOperation(operation, userId)) return false;
            if (nicknameAlreadyTaken) throw new Error(ErrorStatusEnum.NicknameAlreadyTaken);
            const saved = await this.rootStore.apiClient.userController.userControllerUpdateProfile({
                profileFormValuesDto: values,
            });
            if (!this.isCurrentOperation(operation, userId)) return false;
            if (!saved) throw new Error(ErrorStatusEnum.Default);
            runInAction(() => {
                const currentProfile = this.rootStore.authStore.userProfile;
                if (currentProfile) {
                    currentProfile.fullName = values.fullName;
                    currentProfile.nickname = values.nickname;
                }
            });
            return true;
        } catch (error) {
            if (this.isCurrentOperation(operation, userId)) {
                runInAction(() => this.setRequestError(error));
            }
            return false;
        } finally {
            if (this.isCurrentOperation(operation, userId)) {
                runInAction(() => {
                    this.loading = false;
                });
            }
        }
    }

    private isCurrentOperation(operation: number, userId: string | null): boolean {
        return (
            this.operation === operation &&
            this.currentUserId === userId &&
            this.rootStore.authStore.userProfile?.id === userId
        );
    }

    private setRequestError(error: unknown): void {
        const status = error instanceof Error ? error.message : ErrorStatusEnum.Default;
        const key = `errors.${status}`;
        this.error = i18n.exists(key) ? i18n.t(key) : i18n.t(`errors.${ErrorStatusEnum.Default}`);
    }

    reset(): void {
        this.operation += 1;
        this.showSurvey = true;
        this.sourceSaved = false;
        this.fullName = '';
        this.nickname = '';
        this.error = null;
        this.loading = false;
        this.currentUserId = null;
        this.checkout = null;
        this.checkoutPaymentConfirmed = false;
        this.phase = 'idle';
    }
}
